import React from "react";

const TIER_STYLES = {
    none: { glow: "rgba(168,153,139,0.4)", text: "text-[#A8998B]", ring: "ring-white/10", label: "Researcher" },
    bronze: { glow: "rgba(205,127,50,0.55)", text: "text-[#E0996D]", ring: "ring-[#CD7F32]/40", label: "Bronze" },
    silver: { glow: "rgba(200,200,208,0.55)", text: "text-[#D4D4DE]", ring: "ring-[#C8C8D0]/40", label: "Silver" },
    gold: { glow: "rgba(233,188,11,0.7)", text: "text-[var(--gold)]", ring: "ring-[var(--gold)]/50", label: "Gold" },
    platinum: { glow: "rgba(229,228,226,0.65)", text: "text-white", ring: "ring-white/40", label: "Platinum" },
    diamond: { glow: "rgba(125,232,255,0.65)", text: "text-[#7DE8FF]", ring: "ring-[#7DE8FF]/50", label: "Diamond" },
};

export const VipBadge = ({ tier = "none", size = "md" }) => {
    const s = TIER_STYLES[tier] || TIER_STYLES.none;
    const dims = size === "sm" ? "text-[10px] px-2 py-0.5" : "text-xs px-3 py-1";
    return (
        <span
            className={`inline-flex items-center gap-1.5 ${dims} rounded-full bg-black/40 ring-1 ${s.ring} ${s.text} font-mono uppercase tracking-widest`}
            style={{ boxShadow: `0 0 14px ${s.glow}` }}
            data-testid={`vip-badge-${tier}`}
        >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.glow, boxShadow: `0 0 6px ${s.glow}` }} />
            {s.label}
        </span>
    );
};

export const VipTierCard = ({ tier = "none", lifetimeSpend = 0, progress, tiers = [] }) => {
    const s = TIER_STYLES[tier] || TIER_STYLES.none;
    const next = progress?.next;
    const remaining = progress?.remaining_to_next || 0;
    const currentThreshold = progress?.current?.threshold || 0;
    const nextThreshold = next?.threshold || currentThreshold;
    const span = Math.max(1, nextThreshold - currentThreshold);
    const pct = next ? Math.min(100, Math.max(0, ((lifetimeSpend - currentThreshold) / span) * 100)) : 100;

    return (
        <div
            className="relative glass-elevated rounded-2xl p-6 overflow-hidden"
            style={{ background: "radial-gradient(circle at top right, rgba(233,188,11,0.08), transparent 60%)" }}
            data-testid="vip-tier-card"
        >
            <div className="absolute inset-0 pointer-events-none opacity-30" style={{ background: `radial-gradient(circle at 80% 20%, ${s.glow}, transparent 50%)` }} />
            <div className="relative">
                <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
                    <div>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">VIP Status</p>
                        <h3 className="font-display text-3xl font-extrabold text-white italic tracking-tighter">
                            {s.label} <span className={s.text}>·</span> <span className={s.text}>{progress?.current?.discount_pct || 0}%</span>
                        </h3>
                        <p className="text-xs text-[var(--text-secondary)] mt-1">Permanent discount on every order, applied automatically when signed in.</p>
                    </div>
                    <VipBadge tier={tier} />
                </div>

                <div className="grid grid-cols-2 gap-3 mb-5">
                    <div className="rounded-xl bg-black/40 border border-white/5 p-3">
                        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Lifetime spend</p>
                        <p className="font-mono text-[var(--gold)] text-lg font-bold" data-testid="lifetime-spend">A${lifetimeSpend.toFixed(2)}</p>
                    </div>
                    <div className="rounded-xl bg-black/40 border border-white/5 p-3">
                        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">{next ? `To ${next.label}` : "Top tier reached"}</p>
                        <p className="font-mono text-white text-lg font-bold" data-testid="remaining-to-next">
                            {next ? `A$${remaining.toFixed(2)}` : "MAX"}
                        </p>
                    </div>
                </div>

                {next && (
                    <>
                        <div className="h-2 rounded-full bg-white/5 overflow-hidden mb-2">
                            <div
                                className="h-full rounded-full transition-all"
                                style={{
                                    width: `${pct}%`,
                                    background: `linear-gradient(90deg, ${s.glow}, var(--gold))`,
                                    boxShadow: `0 0 12px ${s.glow}`,
                                }}
                                data-testid="vip-progress-bar"
                            />
                        </div>
                        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] text-right">
                            {pct.toFixed(0)}% to {next.label}
                        </p>
                    </>
                )}

                <div className="mt-5 pt-4 border-t border-white/5">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2">All tiers</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                        {tiers.filter((t) => t.slug !== "none").map((t) => {
                            const isCurrent = t.slug === tier;
                            return (
                                <div
                                    key={t.slug}
                                    className={`rounded-lg p-2 border ${isCurrent ? "border-[var(--gold)]/50 bg-[rgba(233,188,11,0.05)]" : "border-white/5 bg-black/30"}`}
                                >
                                    <p className={`text-[10px] font-mono uppercase tracking-widest ${isCurrent ? "text-[var(--gold)]" : "text-[var(--text-muted)]"}`}>
                                        {t.label}
                                    </p>
                                    <p className="text-xs text-white font-bold mt-0.5">{t.discount_pct}%</p>
                                    <p className="text-[10px] text-[var(--text-muted)]">A${t.threshold}+</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};
